// Hook for managing payment plan data and API calls
import { useCallback, useState } from 'react';
import { useAppDispatch } from 'store/hooks';
import {
  paymentUpdateScheduleData,
  leadData,
  getPaymentPlanDates,
  getScheduleInterval,
} from 'store/slices/dashboardSlice';
import { oldPaymentMethodsGet } from 'store/slices/paymentMethodSlice';
import {
  GetPaymentPlanPayload,
  LeadPayload,
  OldPaymentMethodPayload,
  PaymentSchedulePayload,
  ScheduleIntervalPayload,
  Transaction,
} from 'types/updatePaymentPlan';

interface UsePaymentPlanStateReturn {
  isLoading: boolean;
  onGetPaymentData: (id: string) => Promise<void>;
  onGetLeadData: (payload: LeadPayload) => Promise<void>;
  onOldCardDataGet: (payload: OldPaymentMethodPayload) => Promise<void>;
  handleDebouncedChange: () => void;
  getPaymentPlanDates: (payload: GetPaymentPlanPayload) => Promise<void>;
  getScheduleDates: (schedule: string, transactions: Transaction[]) => Promise<void>;
}

export const usePaymentPlanDataState = (
  onDataReceived: (data: any) => void,
  setIsApiCall: (value: boolean) => void
): UsePaymentPlanStateReturn => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const onGetPaymentData = useCallback(
    async (id: string) => {
      setIsLoading(true);
      try {
        const payload: PaymentSchedulePayload = { slug: id };
        const res: any = await dispatch(paymentUpdateScheduleData(payload)).unwrap();
        if (res?.body?.success) {
          onDataReceived(res?.body?.data);
        }
      } catch (error: any) {
        console.log('get payment history err: -->', error?.message);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, onDataReceived]
  );

  const onGetLeadData = useCallback(
    async (payload: LeadPayload) => {
      try {
        await dispatch(leadData(payload)).unwrap();
      } catch (error: any) {
        console.log('onGetLeadData error', error);
      }
    },
    [dispatch]
  );

  const onOldCardDataGet = useCallback(
    async (payload: OldPaymentMethodPayload) => {
      try {
        await dispatch(oldPaymentMethodsGet(payload)).unwrap();
      } catch (error: any) {
        console.log('old card data get erro:-->', error?.message);
      }
    },
    [dispatch]
  );

  const handleDebouncedChange = useCallback(() => {
    setIsApiCall(true);
  }, [setIsApiCall]);

  const getPaymentPlanDatesInternal = useCallback(
    async (payload: GetPaymentPlanPayload) => {
      setIsLoading(true);
      try {
        const res: any = await dispatch(getPaymentPlanDates(payload)).unwrap();
        if (res.status === 200) {
          onDataReceived(res.body.data);
        }
      } catch (error) {
        console.error('Error getting payment plan dates:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, onDataReceived]
  );

  const getScheduleDatesTasks = useCallback(
    async (schedule: string, transactions: Transaction[]) => {
      const payload: ScheduleIntervalPayload = {
        schedule,
        dateArray: transactions.map((payment: any) => ({
          amount: payment?.amount,
          balance: payment?.balance,
          fee: payment?.fee,
          payment_date: payment?.date,
        })),
      };
      setIsLoading(true);
      try {
        const res: any = await dispatch(getScheduleInterval(payload)).unwrap();
        if (res.status === 200) {
          onDataReceived(res.body.data);
        }
      } catch (error) {
        console.error('Error getting schedule dates:', error);
      } finally {
        setIsLoading(false);
        setIsApiCall(false);
      }
    },
    [dispatch, onDataReceived, setIsApiCall]
  );

  return {
    isLoading,
    onGetPaymentData,
    onGetLeadData,
    onOldCardDataGet,
    handleDebouncedChange,
    getPaymentPlanDates: getPaymentPlanDatesInternal,
    getScheduleDates: getScheduleDatesTasks,
  };
};
